import { forwardRef, useState, useRef, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
  /** Allow multiline content with max-width instead of whitespace-nowrap */
  wide?: boolean;
  /** Use portal to render tooltip at document body level (avoids overflow clipping) */
  portal?: boolean;
}

const positionClasses = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
};

const arrowClasses = {
  top: 'top-full left-1/2 -translate-x-1/2 border-t-dark-700 border-l-transparent border-r-transparent border-b-transparent',
  bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-dark-700 border-l-transparent border-r-transparent border-t-transparent',
  left: 'left-full top-1/2 -translate-y-1/2 border-l-dark-700 border-t-transparent border-b-transparent border-r-transparent',
  right: 'right-full top-1/2 -translate-y-1/2 border-r-dark-700 border-t-transparent border-b-transparent border-l-transparent',
};

const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  ({ content, children, position = 'top', className, wide = false, portal = false }, ref) => {
    const [isVisible, setIsVisible] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const [portalPosition, setPortalPosition] = useState({ top: 0, left: 0 });

    // Calculate position for portal mode
    useEffect(() => {
      if (portal && isVisible && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;

        let top = 0;
        let left = 0;

        switch (position) {
          case 'top':
            top = rect.top + scrollY - 8; // 8px gap
            left = rect.left + scrollX + rect.width / 2;
            break;
          case 'bottom':
            top = rect.bottom + scrollY + 8;
            left = rect.left + scrollX + rect.width / 2;
            break;
          case 'left':
            top = rect.top + scrollY + rect.height / 2;
            left = rect.left + scrollX - 8;
            break;
          case 'right':
            top = rect.top + scrollY + rect.height / 2;
            left = rect.right + scrollX + 8;
            break;
        }

        setPortalPosition({ top, left });
      }
    }, [portal, isVisible, position]);

    const tooltipContent = (
      <div
        className={cn(
          'rounded-lg bg-dark-700 px-3 py-2 text-sm text-white shadow-lg',
          'animate-in fade-in-0 zoom-in-95',
          // `max-w` : sur telephone, l'infobulle large ne depasse pas l'ecran.
          wide ? 'w-80 max-w-[calc(100vw-1rem)] whitespace-normal' : 'whitespace-nowrap',
          portal ? 'fixed z-[9999]' : 'absolute z-50',
          !portal && positionClasses[position]
        )}
        style={portal ? {
          top: portalPosition.top,
          left: portalPosition.left,
          transform: position === 'top' ? 'translate(-50%, -100%)' :
                     position === 'bottom' ? 'translate(-50%, 0)' :
                     position === 'left' ? 'translate(-100%, -50%)' :
                     'translate(0, -50%)',
        } : undefined}
        role="tooltip"
      >
        {content}
        {!portal && (
          <span
            className={cn(
              'absolute h-0 w-0 border-4',
              arrowClasses[position]
            )}
          />
        )}
      </div>
    );

    return (
      <div
        ref={(node) => {
          // Handle both refs
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
          (triggerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        className={cn('relative inline-flex items-center', className)}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
      >
        {children}

        {isVisible && (
          portal
            ? createPortal(tooltipContent, document.body)
            : tooltipContent
        )}
      </div>
    );
  }
);

Tooltip.displayName = 'Tooltip';

export { Tooltip };
